import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, Search, Check, Loader2, FolderOpen, ChevronRight, Image as ImageIcon } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function R2BrowseDialog({ onImport, onClose, maxSelect = 12 }) {
  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [prefix, setPrefix] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [nextToken, setNextToken] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [importing, setImporting] = useState(false);

  const fetchObjects = useCallback(async (token = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (prefix) params.append('prefix', prefix);
      if (search) params.append('search', search);
      if (token) params.append('continuation_token', token);
      const { data } = await api.get(`/admin/r2/browse?${params}`);
      if (token) {
        setObjects(prev => [...prev, ...(data.objects || [])]);
      } else {
        setObjects(data.objects || []);
      }
      setNextToken(data.next_token || '');
      setHasMore(data.truncated || false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to browse R2');
    } finally {
      setLoading(false);
    }
  }, [prefix, search]);

  useEffect(() => {
    const timer = setTimeout(() => fetchObjects(), 300);
    return () => clearTimeout(timer);
  }, [fetchObjects]);

  const toggleSelect = (key) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else if (next.size < maxSelect) {
        next.add(key);
      } else {
        toast.error(`Max ${maxSelect} images can be selected`);
      }
      return next;
    });
  };

  const handleImport = async () => {
    if (!selected.size) return;
    setImporting(true);
    await onImport(Array.from(selected));
    setImporting(false);
  };

  // Extract folder prefixes from objects
  const folderPrefixes = [...new Set(
    objects
      .map(o => {
        const parts = o.key.split('/');
        return parts.length > 1 ? parts.slice(0, -1).join('/') + '/' : null;
      })
      .filter(Boolean)
  )];

  const navigateToFolder = (folder) => {
    setPrefix(folder);
    setSelected(new Set());
  };

  const breadcrumbs = prefix ? prefix.split('/').filter(Boolean) : [];

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={onClose}>
      <div
        data-testid="r2-browse-dialog"
        className="bg-neutral-950 border border-neutral-800 rounded-lg shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-neutral-800">
          <div className="flex items-center gap-2">
            <FolderOpen size={18} className="text-neutral-400" />
            <h2 className="text-sm font-semibold text-white uppercase tracking-widest">Import from R2</h2>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-white"><X size={18} /></button>
        </div>

        {/* Search + Breadcrumbs */}
        <div className="p-3 border-b border-neutral-800 space-y-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <Input
              data-testid="r2-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by filename..."
              className="bg-neutral-900 border-neutral-800 text-white text-sm pl-9 h-9"
            />
          </div>
          {/* Breadcrumbs */}
          <div className="flex items-center gap-1 text-xs text-neutral-500 flex-wrap">
            <button
              onClick={() => { setPrefix(''); setSelected(new Set()); }}
              className="hover:text-white transition-colors"
            >
              Bucket
            </button>
            {breadcrumbs.map((crumb, i) => (
              <span key={i} className="flex items-center gap-1">
                <ChevronRight size={10} />
                <button
                  onClick={() => navigateToFolder(breadcrumbs.slice(0, i + 1).join('/') + '/')}
                  className="hover:text-white transition-colors"
                >
                  {crumb}
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 min-h-[300px]">
          {loading && !objects.length ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 size={24} className="animate-spin text-neutral-500" />
            </div>
          ) : objects.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-neutral-500 gap-2">
              <ImageIcon size={32} />
              <p className="text-sm">No images found{search ? ` matching "${search}"` : ''}</p>
            </div>
          ) : (
            <>
              {/* Thumbnail grid */}
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                {objects.map((obj) => {
                  const isSelected = selected.has(obj.key);
                  const filename = obj.key.split('/').pop();
                  return (
                    <button
                      key={obj.key}
                      data-testid={`r2-item-${filename}`}
                      onClick={() => toggleSelect(obj.key)}
                      className={`relative group rounded border overflow-hidden transition-all text-left ${
                        isSelected
                          ? 'border-blue-500 ring-2 ring-blue-500/30'
                          : 'border-neutral-800 hover:border-neutral-600'
                      }`}
                    >
                      <div className="aspect-square bg-neutral-900 relative">
                        <img
                          src={`${API_URL}${obj.thumbnail_url}`}
                          alt={filename}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        {isSelected && (
                          <div className="absolute inset-0 bg-blue-600/30 flex items-center justify-center">
                            <div className="bg-blue-500 rounded-full p-1">
                              <Check size={14} className="text-white" />
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="p-1.5 bg-neutral-900">
                        <p className="text-[9px] text-neutral-400 truncate" title={obj.key}>
                          {filename}
                        </p>
                        <p className="text-[8px] text-neutral-600">
                          {formatSize(obj.size)}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Load more */}
              {hasMore && (
                <div className="flex justify-center mt-4">
                  <Button
                    onClick={() => fetchObjects(nextToken)}
                    disabled={loading}
                    variant="outline"
                    size="sm"
                    className="text-xs border-neutral-700 text-neutral-400 hover:text-white"
                  >
                    {loading ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                    Load More
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-3 border-t border-neutral-800">
          <span className="text-xs text-neutral-500">
            {selected.size > 0 ? `${selected.size} selected` : `${objects.length} images`}
            {maxSelect < 12 && ` (max ${maxSelect} more)`}
          </span>
          <div className="flex gap-2">
            <Button onClick={onClose} variant="ghost" size="sm" className="text-xs text-neutral-400">
              Cancel
            </Button>
            <Button
              data-testid="r2-import-button"
              onClick={handleImport}
              disabled={!selected.size || importing}
              size="sm"
              className="bg-white text-black hover:bg-neutral-200 text-xs uppercase tracking-widest gap-1"
            >
              {importing ? <Loader2 size={14} className="animate-spin" /> : <FolderOpen size={14} />}
              {importing ? 'Importing...' : `Import ${selected.size || ''}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
